// src/components/SignInPage.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { GOOGLE_CLIENT_ID, API_BASE_URL, TWOFACTOR_API_KEY } from "../config";
import "./SignInPage.css";

const OTP_VALIDITY_SECONDS = 120;

const SignInPage = () => {
  const navigate = useNavigate();
  const [isMobileMode, setIsMobileMode] = useState(false);

  // Email/password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Mobile/OTP
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [step, setStep] = useState(0);

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);

  // -------------------- EFFECTS --------------------
  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (userId) checkBotStatusAndRedirect(userId);

    if (window.google && !tokenClient) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        callback: async (tokenResponse) => {
          try {
            const userInfo = await axios.get(
              "https://www.googleapis.com/oauth2/v3/userinfo",
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.access_token}`,
                },
              }
            );
            const { email, name } = userInfo.data;
            const res = await axios.post(`${API_BASE_URL}/google-login`, {
              email,
              name,
            });
            const userId = res.data.user_id;

            localStorage.setItem("user_id", userId);
            localStorage.setItem("email", res.data.email || email);
            localStorage.setItem("name", res.data.name || name);
            window.dispatchEvent(new Event("storage"));

            checkBotStatusAndRedirect(userId);
          } catch (err) {
            alert("Google sign-in failed");
            console.error(err);
          }
        },
      });
      setTokenClient(client);
    }
  }, [tokenClient]);

  useEffect(() => {
    if (otpExpiry === 0) return;

    const interval = setInterval(() => {
      const remaining = Math.max(Math.floor((otpExpiry - Date.now()) / 1000), 0);
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpExpiry]);

  // -------------------- HELPERS --------------------
  const checkBotStatusAndRedirect = async (userId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/has-started-chat/${userId}`);
      if (res.data.has_started) navigate("/chat");
      else navigate("/form");
    } catch {
      navigate("/form");
    }
  };

  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    return digits.length === 10 ? `91${digits}` : digits;
  };

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const saveUserProblem = async (userId) => {
    // Placeholder for any pre-login logic
    console.log("Saving user problem for user:", userId);
  };

  // -------------------- AUTH FUNCTIONS --------------------
  const triggerGoogleLogin = () => {
    if (tokenClient) tokenClient.requestAccessToken();
    else alert("Google client not ready. Refresh page.");
  };

  const handleEmailLogin = async () => {
    if (!email || !password) return alert("Enter email and password");
    try {
      const res = await axios.post(`${API_BASE_URL}/signin`, { email, password });
      localStorage.setItem("user_id", res.data.user_id);
      localStorage.setItem("email", res.data.email || email);
      localStorage.setItem("name", res.data.name || "");
      window.dispatchEvent(new Event("storage"));
      checkBotStatusAndRedirect(res.data.user_id);
    } catch (err) {
      alert(err?.response?.data?.detail || "Login failed.");
    }
  };

  const sendOtp = async () => {
    if (!mobile) return alert("Enter mobile number");
    setSending(true);
    const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otpCode);
    setOtp("");
    setOtpExpiry(Date.now() + OTP_VALIDITY_SECONDS * 1000);
    setStep(1);

    try {
      await axios.get(
        `https://2factor.in/API/V1/${TWOFACTOR_API_KEY}/SMS/${normalizePhone(
          mobile
        )}/${otpCode}/OTP1`
      );
      alert("OTP sent!");
    } catch {
      alert("Failed to send OTP");
      setStep(0);
    } finally {
      setSending(false);
    }
  };

  const resendOtp = () => {
    setOtp("");
    setGeneratedOtp("");
    setOtpExpiry(0);
    sendOtp();
  };

  const verifyOtpAndSignup = async () => {
    try {
      if (!otp) return alert("Enter OTP");
      if (Date.now() > otpExpiry) {
        alert("OTP expired. Please request a new one.");
        setStep(0);
        return;
      }
      if (otp !== generatedOtp) return alert("Invalid OTP. Please try again.");

      setVerifying(true);

      const res = await axios.post(`${API_BASE_URL}/user/login_or_create`, {
        mobile: normalizePhone(mobile),
        name: "New User",
      });

      const userId = res.data.user_id;
      if (userId) {
        localStorage.setItem("user_id", userId);
        localStorage.setItem("mobile", normalizePhone(mobile));
        if (res.data.name) localStorage.setItem("name", res.data.name);
        if (res.data.email) localStorage.setItem("email", res.data.email);
        window.dispatchEvent(new Event("storage"));
        await saveUserProblem(userId);
        navigate("/");
      } else alert("Login failed. Please try again.");
    } catch (err) {
      console.error(
        "[ERROR] OTP verification/login failed:",
        err?.response?.data || err?.message || err
      );
      alert(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  // -------------------- JSX --------------------
  return (
    <div className="signin-container">
      <div className="signin-box">
        <h2 className="signin-title">Welcome back</h2>

        {!isMobileMode ? (
          <>
            <input
              type="email"
              placeholder="Email address"
              className="signin-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              className="signin-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button onClick={handleEmailLogin} className="signin-button">
              Log in
            </button>
          </>
        ) : (
          <>
            {step === 0 ? (
              <>
                <input
                  type="tel"
                  placeholder="Mobile number"
                  className="signin-input"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
                <button
                  onClick={sendOtp}
                  className="signin-button"
                  disabled={sending || !mobile}
                >
                  {sending ? "Sending..." : "Send OTP"}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  className="signin-input"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                />
                {timeLeft > 0 ? (
                  <p>⏳ OTP valid for {formatTime(timeLeft)}</p>
                ) : (
                  <p style={{ color: "red" }}>OTP expired.</p>
                )}

                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button
                    className="signin-button"
                    onClick={verifyOtpAndSignup}
                    disabled={verifying}
                  >
                    {verifying ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button
                    className="signin-button secondary"
                    onClick={resendOtp}
                    disabled={sending || timeLeft > 0}
                  >
                    {sending ? "Resending..." : "Resend OTP"}
                  </button>
                  <button
                    className="signin-button secondary"
                    onClick={() => {
                      setStep(0);
                      setOtp("");
                      setGeneratedOtp("");
                      setOtpExpiry(0);
                      setTimeLeft(0);
                    }}
                  >
                    Change Number
                  </button>
                </div>
              </>
            )}
          </>
        )}

        <div className="divider">or</div>

        <button onClick={triggerGoogleLogin} className="google-button">
          <img
            src="https://www.svgrepo.com/show/355037/google.svg"
            alt="Google"
            className="google-icon"
          />
          Continue with Google
        </button>

        {!isMobileMode ? (
          <button
            onClick={() => setIsMobileMode(true)}
            className="google-button secondary"
          >
            📱 Continue with Mobile Number
          </button>
        ) : (
          <button
            onClick={() => {
              setIsMobileMode(false);
              setStep(0);
              setOtp("");
              setGeneratedOtp("");
              setOtpExpiry(0);
              setTimeLeft(0);
            }}
            className="signin-button secondary"
          >
            ← Back to Email Login
          </button>
        )}

        <div className="signin-footer">
          Don’t have an account?
          <Link to="/signup" className="signin-link">
            {" "}
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
